/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, DestroyRef, ElementRef, EventEmitter, Inject,
  Input, OnInit, Output, ViewChild, signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { StateService } from '@uirouter/core';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { openLikeList } from '../like-list/community-like-list-dialog.component';
import { StatusDraft } from '../status-form/community-status-form.component';
import { ConfirmActionDialogComponent } from '../actions/confirm-action-dialog.component';
import { FeedItemExtraAction } from './feed-item-extra-action';
import { CommunityReportActionComponent } from '../actions/community-report-action.component';
import { CommunitySubscribeActionComponent } from '../actions/community-subscribe-action.component';
import { HideItemService } from '../actions/hide-item.service';
import { toEmojiHtml, toEmojiText, toPreviewHtml } from '../preview-html';
import { LikeService } from '../../../../services/like.service';
import { highlightHtml } from '../../../../pipes/highlight-html';
import {
  ItemRoute, itemCommentCount, itemGlyph, itemLikeCount, itemRoute, itemTypeLabel,
} from '../community-item';

const NON_SOCIAL_TYPES = ['project', 'referral_campaign', 'course'];
// Same breakpoint as `.feed-item__phone-only` in the stylesheet.
const PHONE_QUERY = '(max-width: 574px)';
let nextId = 0;

// A single card in the community feed.
@Component({
  selector: 'ut-community-feed-item',
  templateUrl: './community-feed-item.component.html',
  styleUrls: ['./community-feed-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityFeedItemComponent implements OnInit, AfterViewInit {
  @Input({ required: true }) item: any;
  @Input({ required: true }) user: any;
  // The single status page: full text, and no inline thread because the page shows the comment section.
  @Input() detail = false;
  // Search terms to mark in the title and preview (search results page).
  @Input() highlight = '';
  @Output() commented = new EventEmitter<any>();
  @Output() deleted = new EventEmitter<any>();

  @ViewChild('content') content: ElementRef<HTMLElement> | undefined;
  // The ⋯ menu drives these on phones, where their buttons are hidden.
  @ViewChild(CommunitySubscribeActionComponent) subscribeAction: CommunitySubscribeActionComponent | undefined;
  @ViewChild(CommunityReportActionComponent) reportAction: CommunityReportActionComponent | undefined;
  // A page's own action projected with `feedItemAction`, offered in the ⋯ menu on phones.
  @ContentChild(FeedItemExtraAction) extraAction: FeedItemExtraAction | undefined;

  preview = '';
  titleHtml = '';
  isSocial = false;
  authorGlyph: string | undefined;
  route: ItemRoute | null = null;
  // "Add comment" opens the thread with the form; "Show more comments" opens it read-only.
  showComments = signal(false);
  showCommentForm = signal(false);
  loadMoreOnOpen = signal(false);
  // True on phones, where the ⋯ menu also holds the actions whose buttons are hidden there.
  isPhone = false;
  isClamped = signal(false);
  expanded = signal(false);
  editing = signal(false);
  saving = signal(false);
  latestReply: { user: any; published_at: string; html: SafeHtml } | null = null;

  // Names the card for screen readers (an sr-only heading, also used to label its action groups).
  readonly headingId = `feedItemHeading${nextId++}`;
  readonly typeLabel = itemTypeLabel;
  readonly likeCount = itemLikeCount;
  readonly commentCount = itemCommentCount;

  constructor(
    @Inject('$filter') private $filter: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    @Inject('StatusService') private StatusService: any,
    @Inject('AlertService') private AlertService: any,
    @Inject('ServerMessages') private ServerMessages: any,
    @Inject('$state') private $state: StateService,
    private dialogService: DialogService,
    private hideItem: HideItemService,
    private likeService: LikeService,
    private destroyRef: DestroyRef,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.buildPreview();
    this.buildTitle();
    this.buildLatestReply();
    this.isSocial = !NON_SOCIAL_TYPES.includes(this.item.type);
    this.authorGlyph = itemGlyph(this.item);
    this.route = itemRoute(this.item);
    if (this.detail) this.expanded.set(true);

    // Those phone-only menu items are only rendered on phones. Hidden with CSS instead, CDK's menu would still make
    // the first one active, focus would silently stay on the trigger and Escape would never reach the menu.
    const phone = window.matchMedia(PHONE_QUERY);
    this.isPhone = phone.matches;
    const onPhoneChange = (event: MediaQueryListEvent) => {
      this.isPhone = event.matches;
      this.cdr.markForCheck();
    };
    phone.addEventListener('change', onPhoneChange);
    this.destroyRef.onDestroy(() => phone.removeEventListener('change', onPhoneChange));

    // The Like button updates the item in place and announces it on LikeService.changed.
    this.likeService.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((likeable) => {
      if (likeable.id === this.item.id && likeable.type === this.item.type) {
        this.cdr.markForCheck();
      }
    });
  }

  ngAfterViewInit(): void {
    this.measureClamp();
  }

  get authorName(): string {
    switch (this.item.type) {
      case 'project':
        return this.item.urgent ? 'Urgent Project' : 'Ongoing Project';
      case 'referral_campaign':
        return 'Referral Campaign';
      case 'course':
        return 'uTest Academy';
      case 'tool':
        return 'Tool';
      default:
        return this.item.user?.name;
    }
  }

  // "Post options" on a status; the item's own type elsewhere ("Article options").
  get optionsLabel(): string {
    return this.item.type === 'status' ? 'Post options' : `${itemTypeLabel(this.item)} options`;
  }

  get heading(): string {
    const type = itemTypeLabel(this.item);
    const title = this.item.title || (this.item.type === 'tool' ? this.item.name : '');
    return title ? `${type}: ${toEmojiText(title)}` : `${type} by ${this.authorName}`;
  }

  get authorRoute(): ItemRoute | null {
    return this.isSocial && this.item.user && this.item.type !== 'tool'
      ? { state: 'profile.about', params: { profileId: this.item.user.slug } }
      : this.route;
  }

  get opensOnClick(): boolean {
    return this.item.type === 'status' && !this.detail;
  }

  // Tools have no comment thread, so their card has no comment button, counts or replies.
  get commentable(): boolean {
    return this.item.type !== 'tool';
  }

  // Not on a link (a mention, say), and not while the tester is selecting text to copy.
  openFromText(event: MouseEvent): void {
    if (!this.opensOnClick || (event.target as HTMLElement).closest('a, button')) return;
    if (window.getSelection()?.toString()) return;
    this.$state.go(this.route.state, this.route.params);
  }

  get authorSubtext(): string | null {
    const author = this.item.user;
    if (!this.isSocial || !author) return null;
    if (author.id === this.user.id) return 'You';
    return author.user_title || author.country?.name || null;
  }

  toggleComments(): void {
    if (this.LightTesterErrorService.show()) return;
    const open = !this.showCommentForm();
    this.showCommentForm.set(open);
    if (open && !this.showComments()) {
      this.loadMoreOnOpen.set(false);
      this.showComments.set(true);
    } else if (!open && !this.loadMoreOnOpen()) {
      // Opened only for writing, so closing the form closes the thread again.
      this.showComments.set(false);
    }
  }

  // The "Show more comments" button goes away once the thread opens, so focus moves into the thread.
  showMoreComments(): void {
    this.loadMoreOnOpen.set(true);
    this.showComments.set(true);
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>('.comments')?.focus());
  }

  showLikes(): void {
    openLikeList(this.dialogService, this.item, this.user);
  }

  onCommented(): void {
    this.buildLatestReply();
    this.cdr.markForCheck();
    this.commented.emit(this.item);
  }

  get canSubscribe(): boolean {
    return ['status', 'article', 'topic'].includes(this.item.type) && this.item.user?.id !== this.user.id
      && !!this.item.current_user_permissions?.subscribe;
  }

  get canReport(): boolean {
    return this.isSocial && this.item.user?.id !== this.user.id && !!this.item.current_user_permissions?.flag;
  }

  get hasMenu(): boolean {
    return this.canEdit || this.canDelete || this.canHide;
  }

  // Only phones need the ⋯ menu for these, so the trigger is phone-only when nothing else is in it.
  get hasPhoneMenu(): boolean {
    return !!this.extraAction || this.canSubscribe || this.canReport;
  }

  // Moderators hide / show from the ⋯ menu; the body collapses when item.state becomes 'hidden'.
  get canHide(): boolean {
    return ['status', 'comment', 'article', 'topic'].includes(this.item.type) && !!this.item.current_user_permissions?.hide;
  }

  toggleHidden(): void {
    this.hideItem.confirm(this.item)
      .then((ok) => ok && this.hideItem.toggle(this.item).then(() => this.cdr.markForCheck()))
      .catch(() => this.AlertService.add('There was an error', 'danger'));
  }

  get canEdit(): boolean {
    return this.item.type === 'status' && !!this.item.current_user_permissions?.update;
  }

  get canDelete(): boolean {
    return this.item.type === 'status' && !!this.item.current_user_permissions?.destroy;
  }

  saveStatus(draft: StatusDraft): void {
    this.saving.set(true);
    const status = { ...this.item, content: draft.content };
    this.StatusService.update(status, draft.attached_url).then(() => {
      this.item.content = draft.content;
      this.item.attached_url = draft.attached_url;
      this.buildPreview();
      this.editing.set(false);
      this.expanded.set(false);
      this.measureClamp();
      this.AlertService.add('Status successfully updated');
    }, (response) => {
      this.AlertService.addDanger(this.ServerMessages.prettyMessage(response));
    }).finally(() => {
      this.saving.set(false);
      this.cdr.markForCheck();
    });
  }

  confirmDelete(): void {
    this.dialogService.open(ConfirmActionDialogComponent, {
      data: {
        title: 'Delete this post?',
        text: 'This status will be removed from the community feed. This can\'t be undone.',
        confirmLabel: 'Delete',
        destructive: true,
      },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.StatusService.delete(this.item.id).then(() => {
        this.deleted.emit(this.item);
        this.AlertService.add('Status successfully deleted');
      }, () => this.AlertService.add('There was an error', 'danger'));
    });
  }

  // The newest comment is shown under the post while the comment section is closed.
  private buildLatestReply(): void {
    const comment = this.item.recent_comments?.[0];
    this.latestReply = comment ? {
      user: comment.user,
      published_at: comment.published_at,
      html: this.sanitizer.bypassSecurityTrustHtml(this.$filter('uMarkdown')(comment.content)),
    } : null;
  }

  private buildPreview(): void {
    const raw = ['article', 'topic'].includes(this.item.type)
      ? this.$filter('uMarkdown')(this.item.content)
      : this.item.content || this.item.description || '';
    this.preview = toPreviewHtml(raw, this.$filter, this.item.type === 'status' ? this.item.mentions || {} : undefined);
    this.preview = highlightHtml(this.preview, this.highlight);
  }

  private buildTitle(): void {
    const title = this.item.title || (this.item.type === 'tool' ? this.item.name : '');
    this.titleHtml = title ? highlightHtml(toEmojiHtml(title), this.highlight) : '';
  }

  // The "See more" button is removed once clicked, so focus moves to the text it revealed.
  expand(): void {
    this.expanded.set(true);
    this.content?.nativeElement.focus();
  }

  // Measure after layout so the "See more" toggle only shows for clamped text.
  private measureClamp(): void {
    setTimeout(() => {
      const el = this.content?.nativeElement;
      this.isClamped.set(!!el && el.scrollHeight > el.clientHeight);
    });
  }
}
